import { useEffect, useState } from "react";
import { Search, Building2, Users as UsersIcon, RefreshCw, ChevronRight, Trash2, ArrowLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import { PolicyPortfolioTable } from "../components/PolicyPortfolioTable";
import { PageHead, Panel, LoadingBlock } from "../shell";
import { money, pct, growthClass } from "../lib/format";
import { can } from "../lib/permissions";

const TYPE_LABEL = { CORPORATE: "Kurumsal", INDIVIDUAL: "Bireysel", UNKNOWN: "Belirsiz" };
const TYPES = ["CORPORATE", "INDIVIDUAL", "UNKNOWN"];

export function Customers({ user }) {
  const [tab, setTab] = useState("corporate"); // corporate | list | duplicates
  const [reportData, setReportData] = useState(null);
  const [list, setList] = useState(null);
  const [dups, setDups] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [rebuildBusy, setRebuildBusy] = useState(false);
  const [rebuildNotice, setRebuildNotice] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (tab === "corporate" && !reportData) {
      api.get("/customers-report/corporate?top=20").then((r) => setReportData(r.data));
    }
    if (tab === "duplicates" && !dups) {
      api.get("/customers/possible-duplicates").then((r) => setDups(r.data));
    }
  }, [tab]);

  useEffect(() => {
    if (tab === "list") {
      setList(null);
      const p = { page: 1, page_size: 100, search };
      if (typeFilter) p.customer_type = typeFilter;
      api.get("/customers", { params: p }).then((r) => setList(r.data));
    }
  }, [tab, search, typeFilter]);

  const rebuild = async () => {
    if (!window.confirm("Customer Master ve bağlı analitik katmanlar yedeklenerek yeniden hazırlanacak. Devam edilsin mi?")) return;
    setRebuildBusy(true); setRebuildNotice("");
    try {
      const r = await api.post("/customers/rebuild");
      const stats = r.data.stats || {};
      const customers = Number(stats.customers || 0).toLocaleString("tr-TR");
      const aliases = Number(stats.aliases || 0).toLocaleString("tr-TR");
      const scanned = Number(stats.source_rows || 0).toLocaleString("tr-TR");
      const started = r.data.status === "STARTED";
      setRebuildNotice(`${started ? "Yeniden oluşturma başlatıldı; işlem arka planda tamamlanacak" : "Dry-run tamamlandı"}: ${customers} müşteri · ${aliases} alias · ${scanned} kayıt tarandı`);
      if (!started) {
        setReportData(null); setList(null); setDups(null);
        if (tab === "corporate") api.get("/customers-report/corporate?top=20").then((response) => setReportData(response.data));
      }
    } catch (e) {
      setRebuildNotice("Yeniden oluşturma başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setRebuildBusy(false); }
  };

  return (
    <main className="content">
      <PageHead eyebrow="MÜŞTERİ MASTER" title="Müşteriler / Sigorta Ettiren"
        desc="Kanonik müşteri tanımları, kurumsal müşteri analizi ve olası eşleştirmeler."
        actions={
          can(user,"customers.manage") && (
            <button data-testid="customers-rebuild-button" className="outline-button" onClick={rebuild} disabled={rebuildBusy}>
              <RefreshCw size={14}/> {rebuildBusy ? "Yeniden Oluşturuluyor..." : "Yeniden Oluştur"}
            </button>
          )
        } />
      {rebuildNotice && <div className="save-notice" data-testid="customers-rebuild-notice" style={{ marginBottom: 12 }}>{rebuildNotice}</div>}

      <div className="filters" style={{ gap: 8 }}>
        <button data-testid="customers-tab-corporate" className={tab === "corporate" ? "selected" : ""} onClick={() => setTab("corporate")}>
          <Building2 size={13} style={{ marginRight: 4 }}/> Kurumsal Müşteri Analizi
        </button>
        <button data-testid="customers-tab-list" className={tab === "list" ? "selected" : ""} onClick={() => setTab("list")}>
          <UsersIcon size={13} style={{ marginRight: 4 }}/> Tüm Müşteriler
        </button>
        <button data-testid="customers-tab-duplicates" className={tab === "duplicates" ? "selected" : ""} onClick={() => setTab("duplicates")}>
          Olası Eşleştirmeler
        </button>
      </div>

      {tab === "corporate" && (!reportData ? <LoadingBlock/> : (
        <>
          <div className="period-caption">Rapor tarihi aralığı: {reportData.year}-01-01 — {reportData.period_end}</div>
          <section className="lower-grid">
            <Panel title="Net Prim'e Göre İlk 20 Kurumsal Müşteri" sub="Cari dönem / önceki yıl karşılaştırması">
              <table data-testid="corp-top-premium">
                <thead><tr>
                  <th>#</th><th>Kurumsal Müşteri</th><th style={{textAlign:"right"}}>Net Prim</th>
                  <th style={{textAlign:"right"}}>Komisyon</th><th style={{textAlign:"right"}}>Efektif %</th>
                  <th style={{textAlign:"right"}}>Poliçe</th><th style={{textAlign:"right"}}>Şirket</th>
                  <th style={{textAlign:"right"}}>GY Prim</th><th>Büyüme</th>
                </tr></thead>
                <tbody>{reportData.top_by_premium.map((r, i) => (
                  <tr key={r.customer_id} className="clickable" onClick={() => navigate(`/customers/${r.customer_id}`)}>
                    <td>{i+1}</td>
                    <td><strong>{r.customer_name}</strong>{r.vkn && <span className="muted" style={{ fontSize: 10, marginLeft: 6 }}>VKN {r.vkn}</span>}</td>
                    <td style={{textAlign:"right"}}>{money(r.premium, true)}</td>
                    <td style={{textAlign:"right"}}>{money(r.commission, true)}</td>
                    <td style={{textAlign:"right"}}>{pct(r.effective_rate, 2)}</td>
                    <td style={{textAlign:"right"}}>{new Intl.NumberFormat("tr-TR").format(r.policies)}</td>
                    <td style={{textAlign:"right"}}>{r.insurance_companies}</td>
                    <td style={{textAlign:"right"}}>{money(r.prev_premium, true)}</td>
                    <td className={growthClass(r.growth)}>{r.growth !== null && Math.abs(r.growth) < 10000 ? pct(r.growth) : "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </Panel>
          </section>
          <section className="lower-grid">
            <Panel title="Komisyon'a Göre İlk 20 Kurumsal Müşteri">
              <table data-testid="corp-top-commission">
                <thead><tr>
                  <th>#</th><th>Kurumsal Müşteri</th><th style={{textAlign:"right"}}>Komisyon</th>
                  <th style={{textAlign:"right"}}>Net Prim</th><th style={{textAlign:"right"}}>Poliçe</th>
                </tr></thead>
                <tbody>{reportData.top_by_commission.map((r, i) => (
                  <tr key={r.customer_id} className="clickable" onClick={() => navigate(`/customers/${r.customer_id}`)}>
                    <td>{i+1}</td>
                    <td><strong>{r.customer_name}</strong></td>
                    <td style={{textAlign:"right"}}>{money(r.commission, true)}</td>
                    <td style={{textAlign:"right"}}>{money(r.premium, true)}</td>
                    <td style={{textAlign:"right"}}>{new Intl.NumberFormat("tr-TR").format(r.policies)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </Panel>
          </section>
        </>
      ))}

      {tab === "list" && (
        <>
          <div className="filters" style={{ marginTop: -8 }}>
            <div className="searchbox" style={{ margin: 0 }}>
              <Search size={14}/>
              <input data-testid="customers-search" placeholder="Müşteri adı, VKN veya TCKN ara"
                     value={search} onChange={(e) => setSearch(e.target.value)}/>
            </div>
            <select data-testid="customers-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">Tüm tipler</option>
              {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </div>
          {!list ? <LoadingBlock/> : (
            <div className="panel table-panel">
              <div className="period-caption">{list.total.toLocaleString("tr-TR")} kanonik müşteri</div>
              <table data-testid="customers-table">
                <thead><tr>
                  <th>Kanonik Ad</th><th>Tip</th><th>VKN</th><th style={{textAlign:"right"}}>Poliçe Hits</th>
                  <th>Sigorta Şirketleri</th><th></th>
                </tr></thead>
                <tbody>{list.items.map((c) => (
                  <tr key={c.id} className="clickable" onClick={() => navigate(`/customers/${c.id}`)}>
                    <td><strong>{c.canonical_name}</strong></td>
                    <td><span className="tag">{TYPE_LABEL[c.customer_type] || c.customer_type}</span></td>
                    <td>{c.vkn || "—"}</td>
                    <td style={{textAlign:"right"}}>{c.source_hits}</td>
                    <td className="muted" style={{ fontSize: 11 }}>{(c.insurance_companies || []).slice(0, 3).join(", ")}{(c.insurance_companies?.length || 0) > 3 && "…"}</td>
                    <td><ChevronRight size={14}/></td>
                  </tr>
                ))}
                {list.items.length === 0 && <tr><td colSpan={6} className="empty-state">Kayıt yok.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "duplicates" && (!dups ? <LoadingBlock/> : (
        <div className="panel table-panel">
          <div className="period-caption">{dups.groups.length} olası eşleştirme grubu</div>
          <table data-testid="dup-groups-table">
            <thead><tr><th>Karşılaştırma Kimliği</th><th>VKN</th><th>Adaylar</th><th>#</th></tr></thead>
            <tbody>{dups.groups.map((g, i) => (
              <tr key={i}>
                <td><code style={{ fontSize: 11 }}>{g.compare_form}</code></td>
                <td>{g.vkn || "—"}</td>
                <td>
                  {g.customers.slice(0, 4).map((c, j) => (
                    <div key={c.id} style={{ marginBottom: 4 }}>
                      <a className="text-button" onClick={() => navigate(`/customers/${c.id}`)}>{c.canonical_name}</a>
                      <span className="muted" style={{ fontSize: 10, marginLeft: 6 }}>{TYPE_LABEL[c.customer_type]}</span>
                    </div>
                  ))}
                  {g.customers.length > 4 && <div className="muted" style={{ fontSize: 10 }}>+{g.customers.length - 4} tane daha</div>}
                </td>
                <td>{g.count}</td>
              </tr>
            ))}
            {dups.groups.length === 0 && <tr><td colSpan={4} className="empty-state">Olası eşleştirme yok.</td></tr>}
            </tbody>
          </table>
        </div>
      ))}
    </main>
  );
}

export function CustomerDetail({ user }) {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [aliasForm, setAliasForm] = useState("");
  const navigate = useNavigate();

  const load = () => api.get(`/customers/${id}`).then((r) => { setData(r.data); setForm(r.data.customer); });
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    const payload = { canonical_name: form.canonical_name, customer_type: form.customer_type, vkn: form.vkn, tckn: form.tckn };
    await api.patch(`/customers/${id}`, payload);
    setEditing(false); load();
  };
  const addAlias = async () => {
    if (!aliasForm.trim()) return;
    await api.post(`/customers/aliases`, { source_name: aliasForm.trim(), canonical_customer_id: id });
    setAliasForm(""); load();
  };
  const removeAlias = async (aliasId) => {
    await api.delete(`/customers/aliases/${aliasId}`); load();
  };

  if (!data) return <main className="content"><LoadingBlock/></main>;
  const c = data.customer; const k = data.kpis;
  return (
    <main className="content">
      <button className="back-link" onClick={() => navigate("/customers")}><ArrowLeft size={14}/> Tüm müşteriler</button>
      <PageHead eyebrow="MÜŞTERİ DETAYI" title={c.canonical_name}
        desc={`${TYPE_LABEL[c.customer_type]} · ${c.vkn ? "VKN " + c.vkn : c.tckn ? "TCKN " + c.tckn : "kimlik yok"}`}/>

      <section className="kpi-grid">
        <div className="kpi"><div className="kpi-label">Net Prim</div><strong>{money(k.premium, true)}</strong></div>
        <div className="kpi"><div className="kpi-label">Komisyon</div><strong>{money(k.commission, true)}</strong></div>
        <div className="kpi"><div className="kpi-label">Poliçe</div><strong>{new Intl.NumberFormat("tr-TR").format(k.policies)}</strong></div>
        <div className="kpi"><div className="kpi-label">Sigorta Şirketi</div><strong>{k.insurance_companies}</strong></div>
        <div className="kpi"><div className="kpi-label">Poliçe Grubu</div><strong>{k.policy_groups}</strong></div>
      </section>

      {can(user,"customers.edit") && (
        <Panel title="Kanonik Müşteri Kimliği" sub="Ad, tip ve kimlik bilgilerini düzenleyin"
               actions={!editing && <button data-testid="customer-edit-button" className="outline-button" onClick={() => setEditing(true)}>Düzenle</button>}>
          {editing ? (
            <div className="form-grid" style={{ maxWidth: 620 }}>
              <label>Kanonik Ad<input data-testid="customer-name-input" value={form.canonical_name || ""} onChange={(e) => setForm({...form, canonical_name: e.target.value})}/></label>
              <label>Tip<select data-testid="customer-type-select" value={form.customer_type} onChange={(e) => setForm({...form, customer_type: e.target.value})}>
                {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </select></label>
              <label>VKN<input data-testid="customer-vkn-input" value={form.vkn || ""} onChange={(e) => setForm({...form, vkn: e.target.value})}/></label>
              <label>TCKN<input data-testid="customer-tckn-input" value={form.tckn || ""} onChange={(e) => setForm({...form, tckn: e.target.value})}/></label>
              <div style={{ display: "flex", gap: 8 }}>
                <button data-testid="customer-save-button" className="primary-button" onClick={save}>Kaydet</button>
                <button data-testid="customer-edit-cancel" className="outline-button" onClick={() => { setEditing(false); setForm(c); }}>İptal</button>
              </div>
            </div>
          ) : (
            <div className="form-grid readonly" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div><span className="muted">Kanonik Ad</span><strong>{c.canonical_name}</strong></div>
              <div><span className="muted">Tip</span><strong>{TYPE_LABEL[c.customer_type]}</strong></div>
              <div><span className="muted">VKN</span><strong>{c.vkn || "—"}</strong></div>
              <div><span className="muted">TCKN</span><strong>{c.tckn || "—"}</strong></div>
            </div>
          )}
        </Panel>
      )}

      <Panel title={`Alias'lar (${data.aliases.length})`} sub="Bu kanonik müşteriye bağlı ham isim varyasyonları">
        {can(user,"customers.edit") && (
          <div className="filters" style={{ marginBottom: 8 }}>
            <input data-testid="customer-alias-input" placeholder="Yeni alias adı" value={aliasForm} onChange={(e) => setAliasForm(e.target.value)} style={{ padding: 8, minWidth: 260 }}/>
            <button data-testid="customer-alias-add" className="outline-button" onClick={addAlias}>Alias Ekle</button>
          </div>
        )}
        <div className="renewal-table-shell customer-detail-table-shell"><table data-testid="customer-aliases-table">
          <thead><tr><th>Ham İsim</th><th>Kaynak Şirketi</th><th>Eşleştirme Tipi</th><th>Güven</th><th></th></tr></thead>
          <tbody>{data.aliases.map((a) => (
            <tr key={a.id}>
              <td data-label="Ham İsim"><strong>{a.source_name}</strong></td>
              <td data-label="Kaynak Şirketi">{a.source_insurance_company || "—"}</td>
              <td data-label="Eşleştirme Tipi"><span className="tag">{a.match_type}</span></td>
              <td data-label="Güven">{a.match_confidence != null ? `%${(a.match_confidence*100).toFixed(0)}` : "—"}</td>
              <td data-label="İşlem">{can(user,"customers.edit") && a.source !== "AUTO" && (
                <button data-testid={`customer-alias-delete-${a.id}`} className="text-button" onClick={() => removeAlias(a.id)}><Trash2 size={12}/> Sil</button>
              )}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </Panel>

      <Panel title="Son 50 Poliçe" sub="Bu müşteriye ait poliçe hareketleri (canonical + alias)">
        <PolicyPortfolioTable items={data.recent_policies} testId="customer-policies-table" movementMode emptyText="Bu müşteriye ait poliçe hareketi bulunamadı."/>
      </Panel>
    </main>
  );
}
